<template>
  <div
    class="well scrollable xs pl-3 pr-3"
    style="margin-bottom: 1rem">
    <div class="search-box">
      <icon-search />
      <input
        v-model="locationsSearchTerm"
        type="text"
        placeholder="e.g.: Berlin" />
    </div>
    <div
      v-if="isSearchingLocations"
      class="searching">
      <loader-small />
      <span>{{ $t('messages.searchingLocations') }}...</span>
    </div>
    <ul class="location-tree">
      <template
        v-for="item in selectableCountries"
        :key="item.id">
        <location-tree-node
          :selectable-node="item"
          :is-multiselect="isMultiselect"
          :entity="entity"
          :show-sites="showSites"
          :show-screens="showScreens"
          :show-markets="showMarkets"
          :selected-location-i-ds="locationIDs"
          :search-term="locationsSearchTerm"
          :is-organization="true"
          @location-selection-changed="handleLocationSelectionChanged"
          @locations-selected="handleLocationsSelected" />
      </template>
    </ul>
  </div>
</template>

<script>
  import axios from 'axios';
  import { mapActions, mapState } from 'vuex';

  import { LocationTreeNode } from '@/common/components/controls';
  import LoaderSmall from '@/common/components/controls/LoaderSmall';
  import { SelectableNode, TYPE_COUNTRY } from '@/common/components/controls/locationSelector/SelectableNode';
  import { IconSearch } from '@/common/icons/monochrome';
  import Filter from '@/common/models/query-params/Filter';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import * as ComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import QueryParams from '@/common/models/query-params/QueryParams';

  export default {
    components: {
      IconSearch,
      LoaderSmall,
      LocationTreeNode,
    },
    props: {
      locationIDs: {
        type: Array,
        default: () => [],
      },
      siteIDs: {
        type: Array,
        default: () => [],
      },
      entity: {
        type: Object,
      },
      isMultiselect: {
        type: Boolean,
        default: true,
      },
      showSites: {
        type: Boolean,
        default: true,
      },
      showScreens: {
        type: Boolean,
        default: false,
      },
      showMarkets: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['locationSelectionChanged', 'locationsSelected'],
    data() {
      return {
        cancelLocationTreeTokenSource: null,
        countries: [],
        countriesInternal: [],
        isSearchingLocations: false,
        locationsSearchTerm: null,
        locationsSearchTermTimer: null,
        treeSearchLocationsResult: [],
      };
    },
    computed: {
      ...mapState('orgApp', {
        organizationSettings: (state) => state.organizationSettings,
        locationTreeSearchResults: (state) => state.ReferenceData.locationTreeSearchResults,
      }),
      hasSearchTerms() {
        return (
          this.locationsSearchTerm != null &&
          this.locationsSearchTerm.trim() !== '' &&
          this.locationsSearchTerm.trim().length > 1
        );
      },
      selectableCountries() {
        if (this.hasSearchTerms) {
          return this.treeSearchLocationsResult;
        } else {
          return this.countriesInternal;
        }
      },
    },
    watch: {
      countries() {
        if (this.countriesInternal.length === 0) {
          this.countriesInternal = this.countries.map((x) => new SelectableNode(TYPE_COUNTRY, x));
        }
      },
      locationTreeSearchResults() {
        if (this.locationTreeSearchResults.count > 1) {
          // Populate the location tree
          this.treeSearchLocationsResult = (this.locationTreeSearchResults.locations || []).map(
            (country) => new SelectableNode(TYPE_COUNTRY, country, false, false, true),
          );
        }
      },
      locationsSearchTerm(val) {
        if (val === null || val.trim() === '' || val.trim().length < 2) {
          this.isSearchingLocations = false;
          return;
        }

        this.isSearchingLocations = true;
        if (this.locationsSearchTermTimer !== null) {
          clearTimeout(this.locationsSearchTermTimer);
        }
        this.locationsSearchTermTimer = setTimeout(() => {
          this.locationsSearchTermTimer = null;
          this.searchLocationsUsingTerm(val);
        }, 500);
      },
    },
    mounted() {
      this.getOrganizationSettings().then(
        () => {
          this.getCountriesAccordingToOrganizationSettings();
        },
        () => {
          // Do nothing
        },
      );
    },
    methods: {
      ...mapActions('orgApp', [
        'getAllLocations',
        'getLocations',
        'treeSearchLocations',
        'getOrganizationSettings',
        'getAllCountries',
      ]),
      getCountriesAccordingToOrganizationSettings() {
        if (this.organizationSettings === null) {
          return;
        }

        if (
          this.organizationSettings.availableLocationIDs !== null &&
          this.organizationSettings.availableLocationIDs.length > 0
        ) {
          this.getLocations(
            QueryParams.for({
              top: null,
              filterCollection: new FilterCollection([
                new Filter('location_id', this.organizationSettings.availableLocationIDs, ComparisonOperator.IN),
              ]),
            }),
          ).then((countries) => {
            this.countries = countries;
            this.setupSelectedCountries();
          });
        } else {
          this.getAllCountries().then((countries) => {
            this.countries = countries;
            this.setupSelectedCountries();
          });
        }
      },
      setupSelectedCountries() {
        const countries = this.selectableCountries;
        countries.forEach((x) => {
          const isSelected = this.locationIDs.some((y) => y === x.id) || this.siteIDs.some((y) => y === x.id);
          x.changeSelectionState(isSelected);
        });
      },
      handleLocationSelectionChanged(selectableNode) {
        this.$emit('locationSelectionChanged', selectableNode);
      },
      handleLocationsSelected(selectableNodes) {
        if (!this.isMultiselect) {
          this.selectableCountries.forEach((x) => x.clearSelection());
          selectableNodes.forEach((x) => {
            x.isSelected = true;
          });
        }
        this.$emit('locationsSelected', selectableNodes);
      },
      searchLocationsUsingTerm(searchTerm) {
        if (this.cancelLocationTreeTokenSource !== null) {
          this.cancelLocationTreeTokenSource.cancel();
        }
        this.cancelLocationTreeTokenSource = axios.CancelToken.source();
        this.treeSearchLocations({
          searchTerm: searchTerm != null ? searchTerm.trim() : null,
          params: {
            network_ids: null,
            country_ids: this.organizationSettings.availableLocationIDs,
            cancelTokenSource: this.cancelLocationTreeTokenSource,
          },
        }).then(
          () => {
            this.cancelLocationTreeTokenSource = null;
            this.isSearchingLocations = false;
          },
          () => {
            this.isSearchingLocations = false;
          },
        );
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .well {
    background: rgba(0, 0, 0, 0.025);
  }

  ul.location-tree {
    padding-left: 0;
    margin-bottom: 0;

    li:last-of-type {
      margin-bottom: -0.6rem;
    }
  }

  .search-box {
    display: flex;
    align-items: center;
    border: solid 0.1rem $borders;
    margin-bottom: 1.6rem;
    border-radius: 0.4rem;
    background: #fff;
    box-shadow: 0 0.1rem 0.1rem rgba(0, 0, 0, 0.1);

    svg {
      margin: auto 1rem;
      fill: #777;
      stroke: #777;
      height: 1.6rem;
    }

    input {
      flex-grow: 1;
      border-width: 0;
      outline: none;
      height: 4rem;
    }
  }

  .searching {
    animation-name: flipVertical;
    animation-duration: 1s;
    animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
    animation-fill-mode: backwards;
    transform-style: preserve-3d;
    margin-bottom: 1rem;
    background: rgba(0, 0, 0, 0.025);
    border: solid 0.1rem $borders;
    border-radius: 0.4rem;
    padding: 1rem 1.6rem;
    font-size: 0.9em;
    display: flex;
    align-items: center;
    span {
      margin-left: 0.6rem;
    }
  }
</style>
